import { useEffect, useRef } from 'react'

/**
 * Lightweight keyboard-shortcut hook for global app shortcuts.
 *
 * Design constraints:
 * - Never swallow keystrokes when the user is typing in an editable surface
 *   (input, textarea, contenteditable, select). The two exceptions are
 *   `Escape` (cancel) and shortcuts opted into via `allowInInput`.
 * - Prefer `key` matching over `code` so the visual hint (`Kbd`) and the
 *   actual trigger key stay in sync regardless of layout.
 * - The handler receives the raw event so the caller can decide whether to
 *   `preventDefault()` — by default we DO preventDefault for matched
 *   non-text-field invocations to avoid double-firing (e.g. `/` putting a
 *   slash into a refocused text field).
 *
 * Reduced-motion intentionally not handled here — that's a CSS-only concern.
 */

export type Modifier = 'mod' | 'meta' | 'ctrl' | 'alt' | 'shift'

export interface ShortcutBinding {
  /** Lowercase key match. Use 'escape' / 'enter' / 'arrowdown' etc. */
  key: string
  /**
   * Optional modifier list. `mod` means "Cmd on Mac, Ctrl elsewhere".
   * An empty list means no modifiers are allowed (any modifier press is
   * ignored — so `?` doesn't fire when the user holds Cmd+Shift+/).
   */
  modifiers?: readonly Modifier[]
  /** Allow firing while focus is inside an editable surface. */
  allowInInput?: boolean
  /** Skip auto-preventDefault. */
  passive?: boolean
}

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  // Radix-UI / shadcn combobox/menu items keep focus on a `[role=menuitem]`
  // — those are NOT typing surfaces.
  return false
}

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform)
}

function matchesModifiers(
  event: KeyboardEvent,
  modifiers: readonly Modifier[] | undefined,
): boolean {
  const wants = new Set(modifiers ?? [])
  const wantsMeta = wants.has('meta') || (wants.has('mod') && isMac())
  const wantsCtrl = wants.has('ctrl') || (wants.has('mod') && !isMac())
  const wantsAlt = wants.has('alt')
  const wantsShift = wants.has('shift')

  if (event.metaKey !== wantsMeta) return false
  if (event.ctrlKey !== wantsCtrl) return false
  if (event.altKey !== wantsAlt) return false
  if (event.shiftKey !== wantsShift) return false
  return true
}

export function useKeyboardShortcut(
  binding: ShortcutBinding | null,
  handler: (event: KeyboardEvent) => void,
): void {
  const handlerRef = useRef(handler)
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  // Join modifiers into a canonical string outside the deps array — React's
  // exhaustive-deps lint forbids complex expressions in the array, and the
  // array identity changes per render in some callers.
  const modifierKey = binding?.modifiers
    ? [...binding.modifiers].sort().join(',')
    : ''

  useEffect(() => {
    if (!binding) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const targetKey = event.key.toLowerCase()
      if (targetKey !== binding.key.toLowerCase()) return
      if (!matchesModifiers(event, binding.modifiers)) return
      if (!binding.allowInInput && isEditableElement(event.target)) return
      if (!binding.passive) event.preventDefault()
      handlerRef.current(event)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    binding?.key,
    binding?.allowInInput,
    binding?.passive,
    modifierKey,
  ])
}

/**
 * Sequence shortcut — "press X, then Y within `windowMs`".
 *
 * Used for Gmail-style nav shortcuts like `g d` (go to dashboard). Each
 * leader keypress resets the timer; the follower fires if it lands inside
 * the window and the leader was seen.
 */
export function useSequenceShortcut(
  leader: string,
  follower: string,
  handler: () => void,
  options?: { windowMs?: number; allowInInput?: boolean },
): void {
  const handlerRef = useRef(handler)
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])
  const windowMs = options?.windowMs ?? 1200
  const allowInInput = options?.allowInInput ?? false

  useEffect(() => {
    let leaderAt: number | null = null
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (!allowInInput && isEditableElement(event.target)) return
      const k = event.key.toLowerCase()
      const now = performance.now()
      if (k === leader.toLowerCase() && !event.shiftKey) {
        leaderAt = now
        return
      }
      if (leaderAt !== null && now - leaderAt <= windowMs) {
        if (k === follower.toLowerCase()) {
          event.preventDefault()
          leaderAt = null
          handlerRef.current()
          return
        }
        // Any other non-leader keypress cancels the pending sequence.
        leaderAt = null
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [leader, follower, windowMs, allowInInput])
}

/**
 * Format a shortcut binding for display in the help overlay. Picks the
 * platform-appropriate modifier glyphs (⌘ / Ctrl).
 */
export function formatShortcut(binding: ShortcutBinding): string[] {
  const out: string[] = []
  const mods = binding.modifiers ?? []
  const mac = isMac()
  for (const m of mods) {
    if (m === 'mod') out.push(mac ? '⌘' : 'Ctrl')
    else if (m === 'meta') out.push(mac ? '⌘' : 'Win')
    else if (m === 'ctrl') out.push('Ctrl')
    else if (m === 'alt') out.push(mac ? '⌥' : 'Alt')
    else if (m === 'shift') out.push('⇧')
  }
  out.push(prettyKey(binding.key))
  return out
}

function prettyKey(key: string): string {
  const map: Record<string, string> = {
    arrowup: '↑',
    arrowdown: '↓',
    arrowleft: '←',
    arrowright: '→',
    escape: 'Esc',
    enter: '⏎',
    ' ': 'Space',
    '/': '/',
  }
  const lower = key.toLowerCase()
  if (map[lower]) return map[lower]
  if (lower.length === 1) return lower.toUpperCase()
  return key.charAt(0).toUpperCase() + key.slice(1)
}
